import { CreatePDF } from '../utils/Utils';
import { loadAttachmentFile } from '../components/patient-attachments/AuthFile';

/**
 * Generic Clinical Report Service
 * 
 * Provides reusable functions for generating clinical report summaries and PDFs.
 * Any clinical module (cranial asymmetry, posture, pain evolution, etc.) can use
 * this service by providing data in the standard reportData format.
 * 
 * reportData format:
 * {
 *   title: string,
 *   patientName: string,
 *   assessmentDate: string,
 *   measurements: [{ label: string, value: string }],
 *   classification: string (optional),
 *   additionalNotes: string (optional),
 * }
 */

/**
 * Generates a human-readable summary text from report data.
 * Designed to be easy to paste into WhatsApp or similar messaging apps.
 * 
 * @param {object} reportData - Standardized report data
 * @returns {string} Formatted summary text
 */
export const generateSummaryText = (reportData) => {
    const lines = [];

    lines.push(reportData.title);
    lines.push('');
    lines.push(`Paciente: ${reportData.patientName}`);
    lines.push('');
    lines.push(`Data da Avaliação:`);
    lines.push(reportData.assessmentDate);

    if (reportData.measurements && reportData.measurements.length > 0) {
        lines.push('');
        lines.push('Medições');
        reportData.measurements.forEach(m => {
            const valueText = m.severity ? `${m.value} (${m.severity})` : m.value;
            lines.push(`• ${m.label}: ${valueText}`);
        });
    }

    if (reportData.classification) {
        lines.push('');
        lines.push('Classificação');
        lines.push(reportData.classification);
    }

    if (reportData.additionalNotes) {
        lines.push('');
        lines.push(reportData.additionalNotes);
    }

    lines.push('');
    lines.push('---');
    lines.push('Gerado por Gabriela');
    lines.push('Gestão de consultórios e clínicas');
    lines.push('https://www.gabriela.app.br');

    return lines.join('\n');
};

/**
 * Copies text to clipboard.
 * 
 * @param {string} text - Text to copy
 * @returns {Promise<boolean>} Whether the copy was successful
 */
export const copyToClipboard = async (text) => {
    try {
        await navigator.clipboard.writeText(text);
        return true;
    } catch (err) {
        // Fallback for older browsers
        try {
            const textArea = document.createElement('textarea');
            textArea.value = text;
            textArea.style.position = 'fixed';
            textArea.style.left = '-9999px';
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand('copy');
            document.body.removeChild(textArea);
            return true;
        } catch (fallbackErr) {
            return false;
        }
    }
};

/**
 * Baixa (ou reaproveita do cache) os anexos que o relatório exibe, para que o
 * DOM oculto já tenha as imagens quando o PDF for gerado: o html2canvas
 * renderiza object URLs sem problema de CORS, mas não espera por downloads.
 *
 * Uma foto que falhar não impede o relatório: ela simplesmente não aparece.
 *
 * @param {function} loader GetPatientAttachmentFile do DataProvider
 * @param {Array<{id: number, size: string}>} requests anexos e versões desejadas
 * @returns {Promise<object>} mapa { "<id>:<size>": "blob:..." }
 */
export const loadReportPhotoUrls = async (loader, requests) => {
    const urls = {};

    if (!loader || !requests || requests.length === 0) return urls;

    await Promise.all(requests.map(request =>
        loadAttachmentFile(loader, request.id, request.size)
            .then((entry) => {
                urls[`${request.id}:${request.size}`] = entry.url;
            })
            .catch(() => {})
    ));

    return urls;
};

/**
 * Generates a PDF from a DOM element reference.
 * Uses the project's existing CreatePDF utility (html2pdf.js).
 * 
 * @param {HTMLElement} contentElement - The DOM element to convert to PDF
 * @param {string} fileName - The desired file name for the PDF
 * @returns {Promise} resolves when the download starts
 */
export const generateReportPDF = (contentElement, fileName) => {
    if (!contentElement) {
        return Promise.resolve();
    }
    return CreatePDF(contentElement, fileName);
};

/**
 * Builds a standardized PDF file name.
 * 
 * @param {string} patientName - Patient name
 * @param {string} reportType - Type of report (e.g., 'assimetria-craniana')
 * @returns {string} Formatted file name
 */
export const buildReportFileName = (patientName, reportType) => {
    const sanitizedName = (patientName || 'paciente').replace(/\s+/g, '_');
    const date = new Date();
    const formattedDate = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    return `${sanitizedName}_${reportType}_${formattedDate}.pdf`;
};
